import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, ArrowLeft } from 'lucide-react';
import JournalEntryPreviewModal from '../journal-entries/JournalEntryPreviewModal';
import { useInvoicePreviewModal } from '../../hooks/useInvoicePreviewModal';
import { getProfitAndLossDetail } from '../../utils/reportApi';
import { formatCurrency } from '../../utils/formatCurrency';
import {
  getReportSourceLabel,
  getReportSourceRoute,
} from '../../utils/reportSourceNavigation';
import type { ProfitAndLossDetailReport, ProfitAndLossDetailSource } from '../../types/report';

export interface ProfitAndLossAccountDetailProps {
  accountId: number;
  accountNumber: string;
  accountName: string;
  startDate: string;
  endDate: string;
  onBack: () => void;
}

const ProfitAndLossAccountDetail = ({
  accountId,
  accountNumber,
  accountName,
  startDate,
  endDate,
  onBack,
}: ProfitAndLossAccountDetailProps) => {
  const navigate = useNavigate();
  const [detail, setDetail] = useState<ProfitAndLossDetailReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { openInvoicePreview, invoicePreviewModal } = useInvoicePreviewModal();
  const [previewJournalEntryId, setPreviewJournalEntryId] = useState<number | null>(null);

  const fetchDetail = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getProfitAndLossDetail({
        account_id: accountId,
        start_date: startDate,
        end_date: endDate,
      });
      setDetail(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load account detail');
      setDetail(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDetail();
  }, [accountId, startDate, endDate]);

  const navigateToRoute = (pathname: string, state?: Record<string, unknown>) => {
    navigate(pathname, state ? { state } : undefined);
  };

  const handleSourceClick = (source: ProfitAndLossDetailSource) => {
    if (source.type === 'INVOICE') {
      openInvoicePreview(source.id);
      return;
    }

    const route = getReportSourceRoute(source);
    if (route) {
      navigateToRoute(route.pathname, route.state);
    }
  };

  const account = detail?.accounts[0] ?? null;
  const transactions = account?.transactions ?? [];
  const periodLabel =
    detail?.start_date != null
      ? `${detail.start_date} to ${detail.end_date}`
      : `Through ${detail?.end_date ?? endDate}`;

  if (loading) {
    return (
      <div className="p-12 flex justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-slate-800" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6">
      <button
        type="button"
        onClick={onBack}
        className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 mb-4"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Income Statement
      </button>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start justify-between gap-4">
          <div className="flex gap-2">
            <AlertCircle className="h-4 w-4 text-red-600 mt-0.5" />
            <p className="text-red-800 text-sm">{error}</p>
          </div>
          <button onClick={fetchDetail} className="text-sm text-red-700 underline shrink-0">
            Retry
          </button>
        </div>
      )}

      {account && (
        <>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                <span className="font-mono">{accountNumber}</span>
                <span className="mx-2 text-gray-300">—</span>
                {accountName}
              </h2>
              <p className="text-sm text-gray-500 mt-0.5">{periodLabel}</p>
            </div>
            <span className="inline-flex items-center gap-1.5 self-start px-3 py-1 text-xs font-medium rounded-full bg-slate-100 text-slate-800">
              Total: {formatCurrency(account.amount)}
            </span>
          </div>

          {transactions.length === 0 ? (
            <p className="text-sm text-gray-500 py-8 text-center">
              No transactions in this period.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[800px] text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-4 py-3 text-left font-medium text-gray-600">Date</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">Type</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">Num</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">Description</th>
                    <th className="px-4 py-3 text-right font-medium text-gray-600">Debit</th>
                    <th className="px-4 py-3 text-right font-medium text-gray-600">Credit</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-600">JE</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {transactions.map((txn) => {
                    const sourceRoute =
                      txn.source?.type !== 'INVOICE' ? getReportSourceRoute(txn.source) : null;
                    const isInvoiceSource = txn.source?.type === 'INVOICE';

                    return (
                      <tr key={`${txn.journal_entry_id}-${txn.txn_date}-${txn.amount}`} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-gray-900">{txn.txn_date}</td>
                        <td className="px-4 py-3 text-gray-600">
                          {getReportSourceLabel(txn.source)}
                        </td>
                        <td className="px-4 py-3">
                          {(isInvoiceSource || sourceRoute) && txn.source?.number ? (
                            <button
                              type="button"
                              onClick={() => handleSourceClick(txn.source!)}
                              className="text-blue-700 hover:underline font-medium"
                            >
                              {txn.source.number}
                            </button>
                          ) : (
                            <span className="text-gray-500">{txn.source?.number ?? '—'}</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-gray-600">{txn.description ?? '—'}</td>
                        <td className="px-4 py-3 text-right font-mono text-gray-900">
                          {txn.posting_type === 'DEBIT' ? formatCurrency(txn.amount) : '—'}
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-gray-900">
                          {txn.posting_type === 'CREDIT' ? formatCurrency(txn.amount) : '—'}
                        </td>
                        <td className="px-4 py-3">
                          <button
                            type="button"
                            onClick={() => setPreviewJournalEntryId(txn.journal_entry_id)}
                            className="text-blue-700 hover:underline font-mono text-xs"
                          >
                            {txn.je_number}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <div className="border-t-2 border-gray-300 pt-4 mt-4">
            <div className="flex justify-between items-center px-4 py-3 bg-gray-50 rounded-lg font-semibold">
              <span className="text-gray-900">Account Total</span>
              <span className="font-mono text-gray-900">{formatCurrency(account.amount)}</span>
            </div>
          </div>
        </>
      )}

      {!account && !error && (
        <div className="py-12 text-center text-gray-500">No data found for this account.</div>
      )}

      {invoicePreviewModal}

      {previewJournalEntryId != null && (
        <JournalEntryPreviewModal
          journalEntryId={previewJournalEntryId}
          onClose={() => setPreviewJournalEntryId(null)}
        />
      )}
    </div>
  );
};

export default ProfitAndLossAccountDetail;
